import { customElement, property, state } from 'lit/decorators.js';
import { SbkLabelMixin } from "@/components/label/label-mixin";
import { inputStyles } from './input.styles';
import { html, LitElement, nothing, PropertyValues } from 'lit';
import {
    BirthdayMaxAgeValidator,
    BirthdayOgsValidator,
    BirthdayValidator,
    BirthdayWstValidator,
    CurrencyValidator,
    DatePlausibilityValidator,
    DateValidator,
    EmailOgsValidator,
    EmailValidator,
    IbanValidator,
    InsuredNumberOgsValidator,
    KvnrValidator,
    MonthYearValidator,
    NextCareerMoveWstValidator,
    NumberOgsValidator,
    NumberValidator,
    NumericValidator,
    PasswordOgsValidator,
    PensionInsuranceValidator,
    PhoneAreaCodeValidator,
    PhoneAreaValidator,
    PhoneNumberValidator,
    RequiredValidator,
    TelephoneNumberValidator,
    UrlValidator,
    Validator,
    WeeklyWorkHoursValidator,
    YearVptValidator,
    ZipOgsValidator,
    NameValidator, MinimumWageValidator
} from "@/components/validators/validators";
import { FormValidationMixin } from "@/components/mixins/form-validation-mixin";
import { labelStyles } from "@/components/label/label.styles";
import { when } from 'lit/directives/when.js';
import { FormControlDependencyMixin } from "@/components/form-control-dependency/form-control-dependency-mixin";
import './input.scss';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

@customElement('sbk-input')
export class SbkInput extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(LitElement))) {

    static formAssociated = true;

    static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

    _internals: ElementInternals;

    @property()
    autocomplete: string = '';

    @property({ type: Boolean })
    disabled: boolean = false

    @property()
    description: string = ''

    @property({ reflect: true })
    name: string = ''

    @property()
    placeholder: string = ''

    @property({ type: Boolean })
    readonly: boolean = false

    @property({ type: Boolean })
    required: boolean = false

    @property()
    size?: 'large' | 'small' = 'large';

    @property()
    type: string = 'text'

    @property({ attribute: 'input-pattern' })
    inputPattern: 'decimal' | 'iban' | '' = ''

    @property({ reflect: true })
    value: string = ''

    @property({ type: Number }) maxlength: number | null = null;

    @state()
    visible: boolean = true

    @property({ type: Boolean })
    evaluateOnKeypress: boolean = false;

    private initialType: string = '';
    private _touched = false;
    private _focusedByClick = false;

    validatorMapper: {
        [key: string]: Validator
    } = {
            'required': RequiredValidator,
            'email': EmailValidator,
            'url': UrlValidator,
            'validateNumber': NumberValidator,
            'validateNumeric': NumericValidator,
            'validateCurrency': CurrencyValidator,
            'validateIban': IbanValidator,
            'validateKvnr': KvnrValidator,
            'validatePhoneArea': PhoneAreaValidator,
            'validatePhoneNumber': PhoneNumberValidator,
            'validateTelephoneNumber': TelephoneNumberValidator,
            'validatePensionInsuranceNumber': PensionInsuranceValidator,
            'validateNumberOgs': NumberOgsValidator,
            'validateZipOgs': ZipOgsValidator,
            'validateBirthdayOgs': BirthdayOgsValidator,
            'validateInsuredNumberOgs': InsuredNumberOgsValidator,
            'validatePasswordOgs': PasswordOgsValidator,
            'validateEmailOgs': EmailOgsValidator,
            'validatePhoneAreaCode': PhoneAreaCodeValidator,
            'validateMonthYear': MonthYearValidator,
            'validateBirthdayMaxAge': BirthdayMaxAgeValidator,
            'validateDatePlausibility': DatePlausibilityValidator,
            'validateBirthday': BirthdayValidator,
            'validateDate': DateValidator,
            'validateWeeklyWorkHours': WeeklyWorkHoursValidator,
            'validateYearVpt': YearVptValidator,
            'validateBirthdayWst': BirthdayWstValidator,
            'validateNextCareerMoveWst': NextCareerMoveWstValidator,
            'validateName': NameValidator,
            'validateMinimumWage': MinimumWageValidator
        }

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
        });
        this.addEventListener('form-control-dependency-hide', (e: Event) => {
            this.hide();
            this.resetValue((e as CustomEvent).detail.isInitialCall);
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });
        this._onMouseDown = this._onMouseDown.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('mousedown', this._onMouseDown);
        this._internals.setFormValue(this.value);
        if (this.inputPattern === 'iban') {
            this.value = this._formatIban(this.value);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('mousedown', this._onMouseDown);
    }

    static get styles() {
        return [
            labelStyles,
            inputStyles
        ];
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        // only need to check changed properties for an expensive computation.
        if (changedProperties.has('value')) {
            this._internals.setFormValue(this.value);
        }
        if (!this.initialType) {
            this.initialType = this.type;
        }
    }

    render() {
        return html`
            <div class="input__wrapper${this.invalid ? ' input__wrapper--error' : ''}${this.disabled ? ' input__wrapper--disabled' : ''}">
                ${this.labelTemplate(`${this.name}-input`, false, this.invalid)}
                ${when(this.description,
            () => html`
                    <sbk-annotation id="${this.name}-annotation" variant="info" ?disabled=${this.disabled}>${unsafeHTML(this.description)}</sbk-annotation>
                `)}
                <div class="input-container">
                    <input id="${this.name}-input"
                           class="input input--${this.size}"
                           type=${this.type}
                           maxlength=${this.maxlength || ''}
                           .value=${this.value}
                           name=${this.name}
                           autocomplete=${this.autocomplete || nothing}
                           aria-describedby="${this.name}-annotation"
                           ?disabled=${this.disabled}
                           ?readonly=${this.readonly}
                           ?required=${this.required}
                           placeholder=${this.placeholder}
                           @change=${this._changeValue}
                           @keydown=${this._onKeyDown}
                           @keyup=${this._onKeyUp}
                           @input=${this._input}
                           @blur=${this._validate}
                    />
                    ${this.renderEyeIcon()}
                </div>
                ${this.invalid ? this.validationErrorTemplate(`${this.name}-annotation`) : ""}
            </div>
        `;
    }

    _input(event: Event) {
        this._touched = true;
        const element = <HTMLInputElement>event.target;
        if (this.inputPattern === 'decimal') {
            element.value = element.value.replace(/[^0-9.,]/g, '');
        }
        if (this.inputPattern === 'iban') {
            element.value = this._formatIban(element.value);
        }
        const inputEvent = new CustomEvent('input-input', {
            detail: { value: element.value },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(inputEvent);

        if (this.evaluateOnKeypress) {
            this._changeValue(event);
        }
    }

    _changeValue(event: Event) {
        const element = <HTMLInputElement>event.target;
        this._internals.setFormValue(element.value);
        this.value = element.value;
        const inputChangeEvent = new CustomEvent('input-change', {
            detail: { value: element.value },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(inputChangeEvent);
    }

    _onMouseDown() {
        this._focusedByClick = true;
    }

    _validate(event: Event) {
        const element = event.target as HTMLInputElement;

        // Only validate if clicked, or if field has been touched and filled
        if (!this._focusedByClick && (!this._touched || !element.value)) {
            return;
        }

        this._focusedByClick = false; // Reset for next blur
        const validators = this.getValidators();
        this.handleErrors(validators, element.value);
        this.dispatchEvent(new CustomEvent('input-blur'));
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(isInitialCall: boolean = false): void {
        if (!isInitialCall && (!this.readonly && !this.disabled)) {
            this.value = '';
        }
        this._internals.setFormValue('');
    }

    setValue(): void {
        this._internals.setFormValue(this.value);
    }

    renderEyeIcon() {
        if (this.initialType === 'password') {
            return html`
                <sbk-round-button icon=${this.type === 'password' ? 'eye-show' : 'eye-hide'}  variant="icon-only" size="small" class="eye-icon-button" @click=${this.togglePassword}></sbk-round-button></sbk-round-button>
            `;
        }
        return '';
    }

    togglePassword() {
        this.type = this.type === 'password' ? 'text' : 'password';
    }

    _onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            const element = <HTMLInputElement>event.target;
            this._internals.setFormValue(element.value);
            this.value = element.value;
            const submitButton: HTMLInputElement | null | undefined = this.closest('form')?.querySelector('input[type="submit"]')
            this._internals.form?.requestSubmit(submitButton);
        }
    }

    _onKeyUp(event: KeyboardEvent) {
        const element = <HTMLInputElement>event.target;

        if (this.inputPattern === 'iban') {
            if (event.code === 'Space') {
                if (element.value.length !== element.selectionEnd) {
                    event.preventDefault()
                }
            } else {
                let position: number | null = element.selectionStart

                if (null === position) {
                    return;
                }

                /* Adjust position for whitespaces */
                if ([5, 10, 15, 20, 25, 30].includes(position)) {
                    position++
                }

                element.value = this._formatIban(element.value);
            }
        }

        const inputKeyEvent = new CustomEvent('input-keyup', {
            detail: { value: element.value },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(inputKeyEvent);
    }

    private _formatIban(value: string) {
        return value.replace(/[^\da-zA-Z]/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim()
    }

    _required(): void {
        this.required = true;
        this.setAttribute('required', 'required');
    }

    optional(): void {
        this.required = false;
        this.removeAttribute('required');
        this.invalid = false;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "sbk-input": SbkInput;
    }
}
